import React from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import { ThroughProvider } from 'react-through';
import { css } from '@emotion/css';
import { ToastProvider } from '@leafygreen-ui/toast';
import classNames from 'classnames';

import { getEOLStatus, unsetFirstAppSaveAction, unsetFirstDraftSaveAction } from 'baas-ui/actions';
import { clearAll } from 'baas-ui/actions';
import * as actions from 'baas-ui/app/actions';
import Footer from 'baas-ui/app/footer';
import Toasts from 'baas-ui/app/toasts';
import loadable from 'baas-ui/common/components/loadable';
import { buildStatusContainer } from 'baas-ui/common/components/status';
import { StatusBanner, Variant } from 'baas-ui/common/components/status';
import { TitleArea } from 'baas-ui/common/components/title';
import { SnippetsContextProvider } from 'baas-ui/common/context/SnippetsContext';
import { FeatureFlag } from 'baas-ui/common/featureSettings';
import { useLocalStorage } from 'baas-ui/common/hooks/use-local-storage';
import usePoller from 'baas-ui/common/hooks/use-poller';
import { docLinks } from 'baas-ui/common/links';
import { activeGuideKey } from 'baas-ui/common/local-storage-keys';
import { ZIndex } from 'baas-ui/common/styles/zIndex';
import {
  GROUP_OWNER,
  STATUS_TYPE_LOCAL_STORAGE,
  STATUS_TYPE_SERVICE,
  STATUS_TYPE_USER_PERMISSIONS,
  TRIGGER_MANAGER,
} from 'baas-ui/constants';
import { STATUS_TYPE_DEPLOYMENT } from 'baas-ui/deploy/constants';
import { DeployContextProvider } from 'baas-ui/deploy/deploy-context';
import DeployStatusBanner from 'baas-ui/deploy/deploy-status-banner';
import UnderstandingDraftsModal from 'baas-ui/deploy/understanding-drafts-modal';
import UnderstandingSaveAndDeployModal from 'baas-ui/deploy/understanding-save-and-deploy-modal';
import { warningHandlerNotify } from 'baas-ui/error_util';
import { TriggersContextProvider } from 'baas-ui/events/context/TriggersContext';
import * as depsActions from 'baas-ui/functions/dependencies/actions';
import GuideRunner from 'baas-ui/guides/runner';
import * as homeActions from 'baas-ui/home/actions';
import { localStorageDisabledErrorMessage } from 'baas-ui/home/constants';
import NotFound from 'baas-ui/home/not-found';
import { AppProduct } from 'baas-ui/home/types';
import { Breadcrumbs, ErrorBoundaryRoute, SideNav, TopNav } from 'baas-ui/nav';
import { RouteResources } from 'baas-ui/nav/route-resources/RouteResources';
import { useSideNav } from 'baas-ui/nav/useSideNav';
import { AsyncDispatch, AsyncDispatchPayload } from 'baas-ui/redux_util';
import {
  getAppState,
  getFirstAppSaveState,
  getFirstDraftSaveState,
  getHomeState,
  getServiceState,
  getSyncState,
  getUserProfileState,
} from 'baas-ui/selectors';
import { getSettingsState } from 'baas-ui/selectors';
import * as svcActions from 'baas-ui/services/actions';
import MigrationStatusBanner from 'baas-ui/settings/migration-status-banner';
import { featureSettings } from 'baas-ui/stitch_ui';
import * as syncActions from 'baas-ui/sync/actions';
import { STATUS_TYPE_DEV_MODE } from 'baas-ui/sync/constants';
import InitialSyncStatusBanner from 'baas-ui/sync/initial-sync-status-banner';
import SyncEventSubscriptionErrorBanner from 'baas-ui/sync/sync-event-subscription-error-banner';
import SyncMigrationBanner from 'baas-ui/sync/sync-migration-banner';
import { EOLStatus } from 'baas-ui/types';
import { RootState } from 'baas-ui/types';
import rootUrl from 'baas-ui/urls';
import { AppDependencies, FeatureSettings, PartialApp, PartialServiceDesc, SyncConfig } from 'admin-sdk';

import './app.less';

const STICKY_BANNER_STATUS_TYPES = [STATUS_TYPE_DEPLOYMENT, STATUS_TYPE_DEV_MODE];

export const BannerStatusContainer = buildStatusContainer({
  filter: (item) => STICKY_BANNER_STATUS_TYPES.includes(item.statusType ?? ''),
});

export const StatusContainer = buildStatusContainer({
  filter: (item) => !STICKY_BANNER_STATUS_TYPES.includes(item.statusType ?? ''),
});

const Dashboard = loadable(() => import(/* webpackChunkName: 'Dashboard' */ '../dashboard'));
const Authentication = loadable(
  () => import(/* webpackChunkName: 'Authentication' */ '../../auth/components/Authentication')
);
const Clusters = loadable(() => import(/* webpackChunkName: 'Clusters' */ '../../services/mongodb/clusters/Clusters'));
const Deploy = loadable(() => import(/* webpackChunkName: 'Deploy' */ '../../deploy/deploy-page'));
const Events = loadable(() => import(/* webpackChunkName: 'Events' */ '../../events/components/Events'));
const Functions = loadable(() => import(/* webpackChunkName: 'Functions' */ '../../functions/components/Functions'));
// TODO(BAAS-31888): remove the graphql page
const GraphQLPage = loadable(() => import(/* webpackChunkName: 'GraphQLPage' */ '../../graphql'));
const RulesPage = loadable(() => import(/* webpackChunkName: 'RulesPage' */ '../../rules'));
const SchemaPage = loadable(() => import(/* webpackChunkName: 'SchemaPage' */ '../../schema'));
const Hosting = loadable(() => import(/* webpackChunkName: 'Hosting' */ '../../hosting/components/Hosting'));
const LogsPage = loadable(() => import(/* webpackChunkName: 'LogsPage' */ '../../logs'));
const Push = loadable(() => import(/* webpackChunkName: 'Push' */ '../../push/PushNotifications'));
const Services = loadable(() => import(/* webpackChunkName: 'Services' */ './Services'));
const Settings = loadable(() => import(/* webpackChunkName: 'Settings' */ '../../settings/settings-page/Settings'));
const ValuesPage = loadable(() => import(/* webpackChunkName: 'Values' */ '../../values'));
const SDKsPage = loadable(() => import(/* webpackChunkName: 'SDKsPage' */ '../../sdks/SDKsPage'));
const SyncPage = loadable(() => import(/* webpackChunkName: 'SyncPage' */ '../../sync/sync-page'));
const EndpointsPage = loadable(() => import(/* webpackChunkName: 'EndpointsPage' */ '../../endpoints/EndpointsPage'));

interface StateProps {
  app?: PartialApp;
  error?: string;
  groupId: string;
  isFullscreen: boolean;
  userIsProjectOwner: boolean;
  userIsTriggerManager: boolean;
  loadingApp: boolean;
  loadingFeatureFlags: boolean;
  enabledFeatureFlags: FeatureFlag[];
  loadServicesError?: string;
  loadSyncConfigError?: string;
  resourceNotFoundError?: string;
  services: PartialServiceDesc[];
  deploymentConfigUrl: string;
  firstAppSave: boolean;
  firstDraftSave: boolean;
  showCloudNav?: boolean;
}

interface DispatchProps {
  clearAppState(): void;
  clearDataSourceErrors(): void;
  clearClusterProvisionToastState(): void;
  load(groupId: string, appId: string): AsyncDispatchPayload<PartialApp>;
  loadServices(groupId: string, appId: string): AsyncDispatchPayload<PartialServiceDesc[]>;
  getEnabledFeatureFlags(groupId: string, appId: string): AsyncDispatchPayload<FeatureSettings>;
  loadEOLStatus(groupId: string, appId: string): AsyncDispatchPayload<EOLStatus>;
  loadDependencies(groupId: string, appId: string): AsyncDispatchPayload<AppDependencies>;
  setResourceNotFoundError(error?: string): void;
  loadSyncConfig(groupId: string, appId: string): AsyncDispatchPayload<SyncConfig>;
  setGroupId(groupId: string): void;
  unsetFirstAppSave(): void;
  unsetFirstDraftSave(): void;
}

export interface RouteParams {
  groupId: string;
  appId: string;
}
export type RouteProps = RouteComponentProps<RouteParams>;

export interface OwnProps {
  clusterStatePoller: ReturnType<typeof usePoller>;
  children: React.ReactNode;
}

export type Props = StateProps & DispatchProps & RouteProps & OwnProps;

const App = ({
  app,
  clearAppState,
  clearClusterProvisionToastState,
  clearDataSourceErrors,
  clusterStatePoller,
  error,
  groupId,
  isFullscreen = false,
  load,
  getEnabledFeatureFlags,
  loadEOLStatus,
  loadingFeatureFlags = false,
  enabledFeatureFlags = [],
  loadingApp = false,
  loadServices,
  loadDependencies,
  loadServicesError,
  loadSyncConfig,
  loadSyncConfigError,
  location,
  match,
  resourceNotFoundError,
  setGroupId,
  setResourceNotFoundError,
  userIsProjectOwner,
  userIsTriggerManager,
  deploymentConfigUrl,
  firstAppSave = false,
  unsetFirstAppSave,
  firstDraftSave = false,
  unsetFirstDraftSave,
  showCloudNav = false,
}: Props) => {
  useSideNav();

  React.useEffect(() => {
    const matchedGroupId = match.params.groupId;
    if (matchedGroupId && matchedGroupId !== groupId) {
      setGroupId(matchedGroupId);
    }
  }, [match.params.groupId]);

  React.useEffect(() => {
    if (resourceNotFoundError) {
      // unset resourceNotFoundError
      setResourceNotFoundError(undefined);
    }
  }, [location]);

  React.useEffect(() => {
    if (!loadingFeatureFlags) {
      featureSettings.setAppFeatures(match.params.appId, enabledFeatureFlags || []);
    }
  }, [loadingFeatureFlags, enabledFeatureFlags]);

  React.useEffect(() => {
    const matchedGroupId = match.params.groupId;
    const appId = match.params.appId;

    const loadEOLStatusAsync = async () => {
      try {
        load(matchedGroupId, appId);
        loadServices(matchedGroupId, appId);
        loadDependencies(matchedGroupId, appId);
        getEnabledFeatureFlags(match.params.groupId, match.params.appId);
        const res = await loadEOLStatus(matchedGroupId, appId);
        const isEOL = res?.isEOL ?? false;
        if (!isEOL) {
          loadSyncConfig(matchedGroupId, appId);
        }
      } catch (err) {
        warningHandlerNotify(err);
      }
    };
    loadEOLStatusAsync();

    return () => {
      clearAppState();
      clearClusterProvisionToastState();
      clearDataSourceErrors();
      featureSettings.clearAppFeatures();
    };
  }, [match.params.groupId, match.params.appId]);

  const [activeGuide, setActiveGuide] = useLocalStorage(activeGuideKey(match.params.appId), {});
  const [localStorageDisabledBanner, setLocalStorageDisabledBanner] = React.useState(window.localStorage == null);
  const [guidePopoverExpanded, setGuidePopoverExpanded] = React.useState(true);

  if (!app && error) {
    return <NotFound />;
  }

  if (!app) {
    return null;
  }

  // SECBUG-665: Block direct URL access to Charts apps
  if (app.product === AppProduct.Charts) {
    return <NotFound />;
  }

  const appUrl = rootUrl.groups().group(app.groupId).apps().app(app.id);

  return (
    <DeployContextProvider>
      <SnippetsContextProvider>
        <TriggersContextProvider>
          <ToastProvider
            // Set a z-index value so toasts will be rendered above the side nav. See LG-4423 for more details.
            portalClassName={css`
              > * {
                z-index: ${ZIndex.Toast};
              }
            `}
          >
            <ThroughProvider>
              <div className="through-provider">
                {/* Through Provider Managers */}
                <TitleArea />
                <DeployStatusBanner />
                <InitialSyncStatusBanner />
                <SyncMigrationBanner />
                <MigrationStatusBanner />
                <Toasts
                  clusterStatePoller={clusterStatePoller}
                  activeGuide={activeGuide}
                  guidePopoverExpanded={guidePopoverExpanded}
                />
                <UnderstandingDraftsModal open={firstDraftSave} onClose={unsetFirstDraftSave} />
                <UnderstandingSaveAndDeployModal
                  deploymentConfigUrl={deploymentConfigUrl}
                  open={firstAppSave}
                  onClose={unsetFirstAppSave}
                />
                {loadSyncConfigError && (
                  <StatusBanner
                    variant={Variant.Error}
                    message={`Error loading state. Please try again. ${loadSyncConfigError}`}
                    statusId="sync-config-load-error-banner"
                    statusType={STATUS_TYPE_DEV_MODE}
                  />
                )}
                <SyncEventSubscriptionErrorBanner />
                {loadServicesError && (
                  <StatusBanner
                    actionLabel="Refresh"
                    action={() => window.location.reload()}
                    variant={Variant.Error}
                    message="Your MongoDB Atlas cluster(s) failed to re-load and you may not have access to certain features. Try refreshing this page."
                    statusId="service-status-banner"
                    statusType={STATUS_TYPE_SERVICE}
                  />
                )}
                {!userIsProjectOwner && !userIsTriggerManager && (
                  <StatusBanner
                    actionLabel="More Info"
                    action={() => window.open(docLinks.Atlas.UserRoles, '_blank')}
                    variant={Variant.Error}
                    message="You do not have the required permissions to edit this application. Editing an application requires the Project Owner or Project Trigger Manager role."
                    statusId="user-permissions-banner"
                    statusType={STATUS_TYPE_USER_PERMISSIONS}
                  />
                )}
                {localStorageDisabledBanner && (
                  <StatusBanner
                    variant={Variant.Warning}
                    clearable
                    message={localStorageDisabledErrorMessage}
                    statusId="localstorage-disabled-banner"
                    statusType={STATUS_TYPE_LOCAL_STORAGE}
                    onClear={() => setLocalStorageDisabledBanner(false)}
                  />
                )}
                {/* Through Provider Managers */}
                {!isFullscreen && !showCloudNav && <TopNav />}
                <div
                  className={classNames({
                    'app-container': !showCloudNav,
                    'app-container-with-new-nav': showCloudNav,
                    'app-container-fullscreen': isFullscreen,
                  })}
                >
                  {!isFullscreen && !showCloudNav && <SideNav />}
                  <div
                    className={classNames({
                      'page-content': !showCloudNav,
                      'page-content-with-new-nav': showCloudNav,
                      'page-content-is-fullscreen': isFullscreen,
                    })}
                  >
                    <div className={showCloudNav ? 'sticky-banner-status-with-new-nav' : 'sticky-banner-status'}>
                      <BannerStatusContainer />
                    </div>
                    {!isFullscreen && (
                      <>
                        <StatusContainer />
                        {!showCloudNav && <Breadcrumbs />}
                      </>
                    )}
                    <Switch>
                      <Redirect exact from={appUrl.get()} to={appUrl.dashboard()} />
                      {resourceNotFoundError && <Route component={NotFound} />}
                      <ErrorBoundaryRoute
                        path={appUrl.dashboard()}
                        render={(routeProps) => (
                          <RouteResources.App>
                            <Dashboard
                              {...routeProps}
                              setActiveGuide={setActiveGuide}
                              setGuidePopoverExpanded={setGuidePopoverExpanded}
                            />
                          </RouteResources.App>
                        )}
                      />
                      <ErrorBoundaryRoute
                        path={appUrl.auth().list()}
                        render={(routeProps) => (
                          <RouteResources.App>
                            <Authentication app={app} {...routeProps} />
                          </RouteResources.App>
                        )}
                      />
                      <ErrorBoundaryRoute
                        path={appUrl.push().list()}
                        render={(routeProps) => (
                          <RouteResources.App>
                            <Push app={app} {...routeProps} />
                          </RouteResources.App>
                        )}
                      />
                      <ErrorBoundaryRoute
                        path={appUrl.sdks().list()}
                        render={() => (
                          <RouteResources.App>
                            <SDKsPage />
                          </RouteResources.App>
                        )}
                      />
                      <ErrorBoundaryRoute path={appUrl.logs().list()} render={() => <LogsPage appUrl={appUrl} />} />
                      <ErrorBoundaryRoute
                        path={[appUrl.values().list(), appUrl.secrets().list(), appUrl.environmentValues().list()]}
                        render={(routeProps) => <ValuesPage {...routeProps} />}
                      />
                      <ErrorBoundaryRoute
                        path={appUrl.services().list()}
                        render={(routeProps) => <Services app={app} {...routeProps} />}
                      />
                      <ErrorBoundaryRoute
                        path={`(${appUrl.functions().list()}|${appUrl.dependencies().list()})`}
                        render={(routeProps) => <Functions app={app} {...routeProps} />}
                      />
                      <ErrorBoundaryRoute
                        path={appUrl.endpoints().list()}
                        render={(routeProps) => <EndpointsPage {...routeProps} />}
                      />
                      <ErrorBoundaryRoute
                        path={appUrl.triggers().list()}
                        render={(routeProps) => <Events {...routeProps} />}
                      />
                      <ErrorBoundaryRoute
                        path={appUrl.settings().list()}
                        render={() => (
                          <RouteResources.App>
                            <Settings
                              providerRegion={app.providerRegion}
                              deploymentModel={app.deploymentModel}
                              clientAppId={app.clientAppId}
                              appId={app.id}
                              groupId={app.groupId}
                            />
                          </RouteResources.App>
                        )}
                      />
                      <ErrorBoundaryRoute
                        path={appUrl.hosting().list()}
                        render={(routeProps) => (
                          <RouteResources.App>
                            <Hosting app={app} {...routeProps} />
                          </RouteResources.App>
                        )}
                      />
                      <ErrorBoundaryRoute
                        path={appUrl.deploy().list()}
                        render={() => (
                          <RouteResources.App>
                            <Deploy
                              appId={app.id}
                              clientAppId={app.clientAppId}
                              groupId={app.groupId}
                              appName={app.name}
                            />
                          </RouteResources.App>
                        )}
                      />
                      <ErrorBoundaryRoute
                        path={appUrl.clusters().list()}
                        render={(routeProps) => (
                          <RouteResources.App>
                            <Clusters app={app} {...routeProps} />
                          </RouteResources.App>
                        )}
                      />
                      <ErrorBoundaryRoute
                        path={[
                          appUrl.rules().default(':dataSourceName'),
                          appUrl.rules().new(':dataSourceName'),
                          appUrl.rules().rule({ dataSourceName: ':dataSourceName', ruleId: ':ruleId' }).get(),
                          appUrl.rules().list(),
                        ]}
                        render={(routeProps) => (
                          <RouteResources.App>
                            <RulesPage {...routeProps} />
                          </RouteResources.App>
                        )}
                      />
                      <ErrorBoundaryRoute
                        path={[appUrl.schemas().schema(':schemaId').get(), appUrl.schemas().root()]}
                        render={(routeProps) => (
                          <RouteResources.App>
                            <SchemaPage {...routeProps} />
                          </RouteResources.App>
                        )}
                      />
                      {
                        // TODO(BAAS-31888): remove this route
                        <ErrorBoundaryRoute
                          path={appUrl.graphql().root()}
                          render={() => <GraphQLPage appUrl={appUrl} />}
                        />
                      }

                      <ErrorBoundaryRoute
                        path={appUrl.sync().root()}
                        render={(routeProps) => (
                          <RouteResources.App>
                            <SyncPage
                              {...routeProps}
                              setActiveGuide={setActiveGuide}
                              setGuidePopoverExpanded={setGuidePopoverExpanded}
                              productType={app.product}
                            />
                          </RouteResources.App>
                        )}
                      />
                      {!loadingApp && <Route component={NotFound} />}
                    </Switch>
                    <Footer />
                  </div>
                  <GuideRunner
                    activeGuide={activeGuide}
                    location={location}
                    setActiveGuide={setActiveGuide}
                    isExpanded={guidePopoverExpanded}
                    setIsExpanded={setGuidePopoverExpanded}
                  />
                </div>
              </div>
            </ThroughProvider>
          </ToastProvider>
        </TriggersContextProvider>
      </SnippetsContextProvider>
    </DeployContextProvider>
  );
};

const mapStateToProps = (state: RootState) => {
  const { app, error, loadingApp, loadingFeatureFlags, enabledFeatureFlags, isFullscreen, resourceNotFoundError } =
    getAppState(state);
  const { svcsById, error: loadServicesError } = getServiceState(state);
  const {
    config: { loadError: loadSyncConfigError },
  } = getSyncState(state);
  const { groupId } = getHomeState(state);
  const userProfile = getUserProfileState(state);
  const { showCloudNav } = getSettingsState(state);

  const userIsTriggerManager =
    !!userProfile &&
    userProfile.roles &&
    userProfile.roles.some((role) => role.groupId === groupId && role.roleName === TRIGGER_MANAGER);

  const userIsProjectOwner =
    !!userProfile &&
    userProfile.roles &&
    userProfile.roles.some((role) => role.groupId === groupId && role.roleName === GROUP_OWNER);

  let deploymentConfigUrl = '';
  if (app) {
    deploymentConfigUrl = rootUrl.groups().group(groupId).apps().app(app.id).deploy().config();
  }

  const firstAppSave = getFirstAppSaveState(state);
  const firstDraftSave = getFirstDraftSaveState(state);

  return {
    app,
    error,
    groupId,
    isFullscreen,
    userIsProjectOwner,
    userIsTriggerManager,
    loadingApp,
    loadingFeatureFlags,
    enabledFeatureFlags,
    loadServicesError,
    loadSyncConfigError,
    resourceNotFoundError,
    services: Object.values(svcsById),
    deploymentConfigUrl,
    firstAppSave,
    firstDraftSave,
    showCloudNav,
  };
};

const mapDispatchToProps = (dispatch: AsyncDispatch) => ({
  dispatch,
  clearAppState: () => dispatch(clearAll()),
  clearDataSourceErrors: () => dispatch(homeActions.clearDataSourceErrors()),
  clearClusterProvisionToastState: () => dispatch(homeActions.clearClusterProvisionToastState()),
  load: (groupId: string, appId: string) => dispatch(actions.loadApp({ groupId, appId })),
  loadServices: (groupId: string, appId: string) => dispatch(svcActions.loadServices({ groupId, appId })),
  getEnabledFeatureFlags: (groupId: string, appId: string) =>
    dispatch(actions.getAppEnabledFeatureFlags({ groupId, appId })),
  loadDependencies: (groupId: string, appId: string) => dispatch(depsActions.loadDependencies({ groupId, appId })),
  setResourceNotFoundError: (error?: string) => dispatch(actions.setResourceNotFoundError(error)),
  loadSyncConfig: (groupId: string, appId: string) => dispatch(syncActions.loadConfig({ appId, groupId })),
  setGroupId: (groupId: string) => dispatch(homeActions.setGroupId(groupId)),
  unsetFirstAppSave: () => dispatch(unsetFirstAppSaveAction()),
  unsetFirstDraftSave: () => dispatch(unsetFirstDraftSaveAction()),
  loadEOLStatus: (groupId: string, appId: string) => dispatch(getEOLStatus({ groupId, appId })),
});

export { App as AppComponent };

export default connect(mapStateToProps, mapDispatchToProps)(App);
